import {Card, CardContent} from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import * as Highcharts from 'highcharts';
import {useEffect, useRef, useState} from 'react';
import {COLORS, EXPORTING} from '../../../utils/constants';
import {groupBy} from '../../../utils/groupBy';
import {
    AGE_GROUP_KEYS,
    CAREGIVER_KEYS,
    COMMUNITY_KEYS,
    latestPerUoa,
    meanOf,
    OBJECTIVES,
    percentageOf,
    sumAnswers
} from '../../../utils/wia';
import {roundTo} from '../../../utils/round';

interface ICompositionData {
    submissions: any[];
    uoa: string | number
}

interface ISubmissionData {
    awareness: number;
    members: number;
    sociability: number;
    information: number;
    home: number;
}

// Each finalità counts a different set of domanda 12 categories.
const BENEFICIARY_KEYS: Record<keyof ISubmissionData, string[]> = {
    home: AGE_GROUP_KEYS,
    information: [...AGE_GROUP_KEYS, ...CAREGIVER_KEYS],
    sociability: AGE_GROUP_KEYS,
    members: CAREGIVER_KEYS,
    awareness: COMMUNITY_KEYS,
};

const FINALITIES = Object.keys(BENEFICIARY_KEYS) as (keyof ISubmissionData)[];

const countBeneficiaries = (submissions: any[]): ISubmissionData =>
    FINALITIES.reduce((counts, finality) => ({
        ...counts,
        [finality]: submissions
            .filter((submission: any) => submission.question4 === OBJECTIVES[finality])
            .reduce((a: number, submission: any) => a + sumAnswers(submission, BENEFICIARY_KEYS[finality]), 0),
    }), {} as ISubmissionData);

const totalOf = (counts: ISubmissionData): number =>
    FINALITIES.reduce((a, finality) => a + counts[finality], 0);

const toPercentages = (counts: ISubmissionData): ISubmissionData =>
    FINALITIES.reduce((percentages, finality) => ({
        ...percentages,
        [finality]: percentageOf(counts[finality], totalOf(counts)),
    }), {} as ISubmissionData);

const Composition = ({submissions, uoa}: ICompositionData) => {
    const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
    const [options, setOptions] = useState<Highcharts.Options>();

    const calculateSubmissions = () => {
        const latest = latestPerUoa(submissions);

        const currentSubmissions = toPercentages(
            countBeneficiaries(latest.filter((submission: any) => submission.uoa_parent_id === uoa))
        );

        const projects: ISubmissionData[] = Object.values(groupBy(latest, 'uoa_parent_id'))
            .map((project: any) => countBeneficiaries(project))
            .filter((counts: ISubmissionData) => totalOf(counts) > 0);

        // The average project is built from the mean count of each finalità, then turned into percentages.
        const allSubmissions = toPercentages(
            FINALITIES.reduce((means, finality) => ({
                ...means,
                [finality]: meanOf(projects.map(project => project[finality])),
            }), {} as ISubmissionData)
        );

        const bars = (finality: keyof ISubmissionData) => [
            roundTo(currentSubmissions[finality]),
            roundTo(allSubmissions[finality]),
        ];

        const dashboardOptions: any = {
            chart: {
                height: 400,
                type: 'column',
                style: {
                    fontFamily: 'Rubik, sans-serif'
                },
                backgroundColor: '#fafafa'
            },
            title: {
                text: 'Composizione delle attività',
                align: 'left'
            },
            xAxis: {
                categories: ['Il mio progetto', 'Media dei progetti']
            },
            yAxis: {
                min: 0,
                max: 100,
                labels: {
                    format: '{value}%'
                },
                title: {
                    text: ''
                },
                stackLabels: {
                    enabled: false,
                },
            },
            tooltip: {
                valueSuffix: '%'
            },
            plotOptions: {
                column: {
                    cursor: 'pointer',
                    stacking: 'percent',
                }
            },
            series: [
                {
                    name: OBJECTIVES.awareness,
                    color: COLORS.orange,
                    data: bars('awareness')
                },
                {
                    name: OBJECTIVES.members,
                    color: COLORS.green,
                    data: bars('members')
                },
                {
                    name: OBJECTIVES.sociability,
                    color: COLORS.yellow,
                    data: bars('sociability')
                },
                {
                    name: OBJECTIVES.information,
                    color: COLORS.red,
                    data: bars('information')
                },
                {
                    name: OBJECTIVES.home,
                    color: COLORS.blue,
                    data: bars('home')
                }
            ],
            exporting: EXPORTING
        };

        setOptions(dashboardOptions);
    };

    useEffect(() => {
        calculateSubmissions();
    }, []);

    return (
        <Card>
            <CardContent>
                {
                    options && (
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={options}
                            ref={chartComponentRef}
                        />
                    )
                }
            </CardContent>
        </Card>
    );
};

export default Composition;
